import React, { useState, useRef } from 'react';
import { Home, MessageSquare, Car, X, TrafficCone, Sparkles, ExternalLink, Settings, Map, Coffee } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Location, ParkingOption } from '../types';
import TrafficModal from './TrafficModal';

interface BottomNavigationProps {
  onTabChange: (tab: string) => void;
  activeTab: string;
  onMyVibeClick?: () => void;
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ onTabChange, activeTab, onMyVibeClick }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<string>('');
  const [showTrafficModal, setShowTrafficModal] = useState(false);
  const lastClickTimeRef = useRef<number>(0);

  const handleTabClick = (tab: string) => {
    // Simple debounce: prevent rapid double-clicks (200ms window)
    const now = Date.now();
    if (now - lastClickTimeRef.current < 200) {
      return;
    }
    lastClickTimeRef.current = now;
    if (tab === 'parking') {
      setModalType('parking');
      setShowModal(true);
    } else if (tab === 'traffic') {
      setShowTrafficModal(true);
    } else if (tab === 'feedback') {
      // Navigate to feedback page instead of showing modal
      onTabChange('feedback');
    } else if (tab === 'myvibe') {
      if (onMyVibeClick) {
        onMyVibeClick();
      }
    } else {
      // Direct call like LocationModal tabs - no delays, no debouncing
      onTabChange(tab);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
  };

  const closeTrafficModal = () => {
    setShowTrafficModal(false);
  };

  // Mock data for the modals
  const popularLocations: Location[] = [
    {
      _id: '1',
      location: 'Bondi Beach',
      latitude: -33.8915,
      longitude: 151.2767,
      category: 'Park',
      busyness: 85,
      vibeRating: 4.5,
      communityRecommendations: [],
      parkingOptions: [],
      capacities: {},
    },
    {
      _id: '2',
      location: 'Sydney Opera House',
      latitude: -33.8568,
      longitude: 151.2153,
      category: 'Entertainment',
      busyness: 92,
      vibeRating: 4.8,
      communityRecommendations: [],
      parkingOptions: [],
      capacities: {},
    },
  ];

  const savedLocations: Location[] = [
    {
      _id: '3',
      location: 'Darling Harbour',
      latitude: -33.8715,
      longitude: 151.2007,
      category: 'Entertainment',
      busyness: 78,
      vibeRating: 4.2,
      communityRecommendations: [],
      parkingOptions: [],
      capacities: {},
    },
  ];

  const parkingRecommendations: ParkingOption[] = [
    {
      id: '1',
      latitude: -33.8568,
      longitude: 151.2153,
      description: 'Opera House Car Park',
      rating: 4.5,
      totalRatings: 156,
      suggestedBy: 'Official',
      isOfficial: true,
    },
    {
      id: '2',
      latitude: -33.8715,
      longitude: 151.2007,
      description: 'Darling Harbour Parking',
      rating: 4.2,
      totalRatings: 89,
      suggestedBy: 'Community',
      isOfficial: false,
    },
  ];

  const renderModalContent = () => {
    switch (modalType) {
      case 'popular':
        return (
          <div className="modal-content">
            <h2>Popular Locations</h2>
            <div className="locations-list">
              {popularLocations.map((location) => (
                <div key={location._id} className="location-item">
                  <div className="location-info">
                    <h3>{location.location}</h3>
                    <span className="category">{location.category}</span>
                    <div className="metrics">
                      <span className="busyness">{location.busyness}% busy</span>
                      <span className="rating">★ {location.vibeRating}</span>
                    </div>
                  </div>
                  <button className="view-button">View</button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'saved':
        return (
          <div className="modal-content">
            <h2>Saved Locations</h2>
            <div className="locations-list">
              {savedLocations.map((location) => (
                <div key={location._id} className="location-item">
                  <div className="location-info">
                    <h3>{location.location}</h3>
                    <span className="category">{location.category}</span>
                    <div className="metrics">
                      <span className="busyness">{location.busyness}% busy</span>
                      <span className="rating">★ {location.vibeRating}</span>
                    </div>
                  </div>
                  <button className="view-button">View</button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="modal-content">
            <h2>Profile</h2>
            <div className="profile-section">
              <div className="profile-header">
                <div className="avatar">👤</div>
                <div className="profile-info">
                  <h3>VibeCheck User</h3>
                  <p>Member since 2024</p>
                </div>
              </div>
              <div className="profile-stats">
                <div className="stat">
                  <span className="stat-number">{savedLocations.length}</span>
                  <span className="stat-label">Saved Locations</span>
                </div>
                <div className="stat">
                  <span className="stat-number">5</span>
                  <span className="stat-label">Reviews</span>
                </div>
                <div className="stat">
                  <span className="stat-number">3</span>
                  <span className="stat-label">Parking Tips</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'parking':
        return (
          <div className="modal-content">
            <h2>Parking Recommendations</h2>
            <div className="parking-list">
              {parkingRecommendations.map((parking) => (
                <div key={parking.id} className="parking-item">
                  <div className="parking-info">
                    <h3>{parking.description}</h3>
                    <div className="parking-meta">
                      <span className="rating">★ {parking.rating}</span>
                      <span className="ratings-count">({parking.totalRatings} ratings)</span>
                      <span className={`source ${parking.isOfficial ? 'official' : 'community'}`}>
                        {parking.isOfficial ? 'Official' : 'Community'}
                      </span>
                    </div>
                  </div>
                  <button className="directions-button">
                    <span>Directions</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'feedback':
        return (
          <div className="modal-content">
            <h2>Share Your Feedback</h2>
            <div className="feedback-form">
              <p className="feedback-intro">
                We'd love to hear your thoughts! Your feedback helps us improve VibeCheck.
              </p>
              
              <div className="feedback-section">
                <h3>📝 Complete Our Feedback Form</h3>
                <p>Help us make VibeCheck better by completing our quick survey!</p>
                <a 
                  href="https://forms.gle/K7QCJ26P4TwqFpjZ9" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="feedback-link-button"
                >
                  <span>Open Feedback Form</span>
                  <ExternalLink size={16} />
                </a>
              </div>

              <div className="feedback-divider">OR</div>

              <div className="feedback-section">
                <h3>📧 Contact Us via Email</h3>
                <p>Send us an email with your thoughts, suggestions, or questions!</p>
                <a 
                  href="mailto:info@vibecheck.au" 
                  className="feedback-link-button email-button"
                >
                  <span>info@vibecheck.au</span>
                  <ExternalLink size={16} />
                </a>
              </div>

              <div className="feedback-divider">OR</div>

              <div className="feedback-section">
                <h3>💬 Message Us on Social Media</h3>
                <p>Send us a DM with your thoughts, suggestions, or just say hi!</p>
                <div className="social-links">
                  <a 
                    href="https://www.instagram.com/vibechecksydney" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link instagram"
                  >
                    <div className="social-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                    <div className="social-info">
                      <span className="social-name">Instagram</span>
                      <span className="social-handle">@vibechecksydney</span>
                    </div>
                  </a>

                  <a 
                    href="https://www.tiktok.com/@vibecheckau" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link tiktok"
                  >
                    <div className="social-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </div>
                    <div className="social-info">
                      <span className="social-name">TikTok</span>
                      <span className="social-handle">@vibecheckau</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <nav className="bottom-navigation">
        <button 
          onTouchStart={(e) => {
            e.stopPropagation();
            if (e.touches.length === 1) {
              handleTabClick('home2');
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleTabClick('home2');
          }} 
          className={`nav-item ${activeTab === 'home2' ? 'active' : ''}`}
        >
          <Home size={24} />
          <span>Home</span>
        </button>

        <button
          onTouchStart={(e) => {
            e.stopPropagation();
            if (e.touches.length === 1) {
              handleTabClick('coffee');
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleTabClick('coffee');
          }}
          className={`nav-item ${activeTab === 'coffee' ? 'active' : ''}`}
        >
          <Coffee size={24} />
          <span>Coffee Map</span>
        </button>

        <button
          onTouchStart={(e) => {
            e.stopPropagation();
            if (e.touches.length === 1) {
              handleTabClick('home');
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleTabClick('home');
          }} 
          className={`nav-item ${activeTab === 'home' ? 'active' : ''}`}
        >
          <Map size={24} />
          <span>Shops</span>
        </button>

        <button
          onTouchStart={(e) => {
            e.stopPropagation();
            if (e.touches.length === 1) {
              handleTabClick('feedback');
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleTabClick('feedback');
          }}
          className={`nav-item ${activeTab === 'feedback' ? 'active' : ''}`}
        >
          <MessageSquare size={24} />
          <span>Feedback</span>
        </button>
      </nav>

      {/* Traffic Modal */}
      <TrafficModal isOpen={showTrafficModal} onClose={closeTrafficModal} />

      {/* Full Screen Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fullscreen-modal-overlay"
            onClick={closeModal}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fullscreen-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{modalType.charAt(0).toUpperCase() + modalType.slice(1)}</h2>
                <button onClick={closeModal} className="close-button">
                  <X size={24} />
                </button>
              </div>
              {renderModalContent()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BottomNavigation;
